import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { merge, of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, filter, switchMap } from 'rxjs/operators';
import { Task, TaskOutcome } from 'src/app/data/model/task';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { TasksService } from 'src/app/data/service/tasks.service';
import { ConfirmationDialogService } from 'src/app/shared/confirmation-dialog/confirmation-dialog.service';
import { RecordReviewContext } from '../record-review';

/**
 * The approval block at the bottom of a record's side panel, where the Publish footer sits on a
 * bill: "Sam asked you to approve the account coding" with Approve and Reject.
 *
 * It finds its own task -- the open approval on this record that the viewer can decide, or else
 * the one they asked for and are waiting on -- so a panel only says which record it shows:
 *
 *   <app-approval-block recordType="invoice" [recordIds]="[invoice.id, invoice.referenceId]">
 *
 * and shows nothing when there is no such approval. What it finds goes into the panel's
 * RecordReviewContext, which is what highlights the fields under review.
 */
@Component({
  selector: 'app-approval-block',
  templateUrl: './approval-block.component.html',
  styleUrls: ['./approval-block.component.scss']
})
export class ApprovalBlockComponent implements OnInit, OnChanges, OnDestroy {
  /** RecordTypes.Code of the record the panel shows. */
  @Input() recordType: string;
  /** Every id the record is known by (a bill's own id and its ledger id). */
  @Input() recordIds: string[] = [];
  /** Prefer this task when several are open, e.g. the one a Tasks card row was opened for. */
  @Input() taskId: string;

  /** After a decision, so the panel can reload or close. */
  @Output() decided = new EventEmitter<Task>();
  /** Whether the block is showing, so the panel can hide what it replaces (Publish). */
  @Output() activeChange = new EventEmitter<boolean>();

  task: Task = null;
  comment = '';
  rejecting = false;
  busy: TaskOutcome | 'cancel' = null;
  error: string;

  private readonly _reload = new Subject<void>();
  private readonly _subs: Subscription[] = [];

  constructor(
    private _tasks: TasksService,
    private _organizations: OrganizationService,
    private _confirmation: ConfirmationDialogService,
    @Optional() private _context: RecordReviewContext
  ) { }

  get organizationId(): string {
    return this._organizations.selectedOrganization;
  }

  get ids(): string[] {
    return (this.recordIds || []).filter(id => !!id).map(id => String(id));
  }

  /** "the account coding", or "this bill". */
  get subject(): string {
    const fields = this.task?.fields || [];
    if (fields.length === 0) { return 'this ' + (this.task?.recordTypeName || 'record').toLowerCase(); }
    const labels = fields.map(f => f.label.toLowerCase());
    return labels.length === 1 ? labels[0] : labels.slice(0, -1).join(', ') + ' and ' + labels[labels.length - 1];
  }

  get asker(): string {
    if (this.task?.source === 'workflow') { return 'A workflow'; }
    return this.task?.assignedByName || 'Someone';
  }

  ngOnInit(): void {
    this._subs.push(this._reload.pipe(
      filter(() => !!this.recordType && this.ids.length > 0 && !!this.organizationId),
      switchMap(() => this._tasks.forRecord(this.organizationId, this.recordType, this.ids).pipe(catchError(() => of([] as Task[]))))
    ).subscribe(tasks => this.show(tasks)));

    this._subs.push(merge(
      this._tasks.changed$.pipe(filter(c => !this.task || c.taskId === this.task.taskId || !c.organizationId || c.organizationId === this.organizationId)),
      this._tasks.refresh$
    ).pipe(debounceTime(300)).subscribe(() => this._reload.next()));

    this._reload.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.recordIds && !changes.recordIds.firstChange) || (changes.recordType && !changes.recordType.firstChange)) {
      this._reload.next();
    }
  }

  ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
    this._context?.set(null);
  }

  private show(tasks: Task[]): void {
    const open = (tasks || []).filter(t => t.status === 'open' && t.kind === 'approval');
    const mine = (t: Task) => t.canAct;
    const preferred = open.find(t => t.taskId === this.taskId && (t.canAct || t.canCancel));
    const next = preferred || open.find(mine) || open.find(t => t.canCancel) || null;

    if (next?.taskId !== this.task?.taskId) {
      this.comment = '';
      this.rejecting = false;
      this.error = null;
    }
    this.task = next;
    this._context?.set(next);
    this.activeChange.emit(!!next);
  }

  approve(): void {
    this.decide('approve');
  }

  startReject(): void {
    this.rejecting = true;
    this.error = null;
  }

  confirmReject(): void {
    if (!this.comment.trim()) {
      this.error = 'Say why, so they know what to change.';
      return;
    }
    this.decide('reject');
  }

  private decide(outcome: TaskOutcome): void {
    if (!this.task || this.busy) { return; }
    this.busy = outcome;
    this.error = null;
    this._tasks.decide(this.organizationId, this.task.taskId, outcome, this.comment.trim() || undefined).subscribe(
      decided => {
        this.busy = null;
        this.decided.emit(decided);
        this._tasks.requestRefresh();
      },
      error => {
        this.busy = null;
        this.error = TasksService.errorMessage(error, 'That could not be saved.').message;
      });
  }

  cancelRequest(): void {
    if (!this.task || this.busy) { return; }
    this._confirmation.confirm('Withdraw this request?',
      `${this.task.assignedToName || 'They'} will no longer be asked to approve ${this.subject}.`, 'Withdraw', 'Keep it', null, 'sm')
      .then(confirmed => {
        if (!confirmed) { return; }
        this.busy = 'cancel';
        this._tasks.cancel(this.organizationId, this.task.taskId).subscribe(
          () => {
            this.busy = null;
            this._tasks.requestRefresh();
          },
          error => {
            this.busy = null;
            this.error = TasksService.errorMessage(error, 'The request could not be withdrawn.').message;
          });
      }, () => { });
  }
}
